import React from 'react';
import { connect } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import ViewTitle from '../../common/layout/ViewTitle';
import AddUserModal from './AddUserModal';
import RoleCell from './RoleCell';
import EmailCell from './EmailCell';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import { selectCurrentRole, canManageUser } from '../../common/auth/roles';
import { listUsers, updateUserRole, removeUser, errorMessage, applyRoleChange } from './api';

function UserManagementView(props) {
  const { jwtToken, baseApiUri, company, currentRole } = props;

  const [users, setUsers] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [toast, setToast] = React.useState(undefined);
  const [pendingRemoval, setPendingRemoval] = React.useState(undefined);

  // Only the most recent listUsers request may update the rows, so a slow response
  // for a previously selected company can't replace the current company's rows.
  const latestRequest = React.useRef(0);

  const load = React.useCallback(() => {
    const request = ++latestRequest.current;
    if (!(jwtToken && baseApiUri && company)) return Promise.resolve();
    return listUsers(baseApiUri, jwtToken, company.id)
      .then((rows) => { if (request === latestRequest.current) setUsers(rows); })
      .catch((err) => { if (request === latestRequest.current) setToast(errorMessage(err, 'Could not load users.')); });
  }, [baseApiUri, jwtToken, company]);

  React.useEffect(() => {
    // Drop the previous company's rows at once so no row action runs against them.
    setUsers([]);
    setPendingRemoval(undefined);
    load();
  }, [load]);

  const handleRoleChange = async (userId, role) => {
    setBusy(true);
    const result = await applyRoleChange(users, userId, role,
      (id, r) => updateUserRole(baseApiUri, jwtToken, company.id, id, r));
    setUsers(result.users);
    if (result.error) setToast(result.error);
    setBusy(false);
  };

  const handleRemove = () => {
    const target = pendingRemoval;
    setPendingRemoval(undefined);
    setBusy(true);
    removeUser(baseApiUri, jwtToken, company.id, target.user_id)
      .then(() => setUsers(users.filter((u) => u.user_id !== target.user_id)))
      .catch((err) => setToast(errorMessage(err, 'Could not remove user.')))
      .finally(() => setBusy(false));
  };

  const columns = [
    { field: 'email', headerName: 'Email', width: 340, renderCell: ({ row }) => <EmailCell row={row} /> },
    {
      field: 'role', headerName: 'Role', width: 200,
      renderCell: ({ row }) => (
        <RoleCell row={row} actorRole={currentRole} disabled={busy}
          onChange={(role) => handleRoleChange(row.user_id, role)} />
      ),
    },
    { field: 'invited', headerName: 'Pending Invite', width: 150, valueGetter: ({ value }) => (value ? 'Yes' : '') },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: ({ row }) => canManageUser(currentRole, row) && (
        <Button size="small" color="error" disabled={busy} onClick={() => setPendingRemoval(row)}>Remove</Button>
      ),
    },
  ];

  return (
    <React.Fragment>
      <ViewTitle title='User management'>
        <AddUserModal actorRole={currentRole} afterSave={load} disabled={busy} onSavingChange={setBusy} />
      </ViewTitle>

      <Card sx={{ width: "100%", "padding": "24px" }}>
        <Box sx={{ "height": "640px" }}>
          <DataGrid rows={users} getRowId={(row) => row.user_id} columns={columns} autoPageSize disableRowSelectionOnClick />
        </Box>
      </Card>

      <ConfirmRemoveDialog open={Boolean(pendingRemoval)} email={pendingRemoval && pendingRemoval.email}
        onConfirm={handleRemove} onCancel={() => setPendingRemoval(undefined)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(undefined)} message={toast} />
    </React.Fragment>
  )
}

function mapStateToProps(state) {
  return {
    jwtToken: state.common.jwtToken,
    baseApiUri: state.common.baseApiUri,
    company: state.common.company,
    currentRole: selectCurrentRole(state),
  };
}

export default connect(mapStateToProps)(UserManagementView)
